import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Plus, Lock, Sparkles, X } from "lucide-react";
import { HoloCard, NeonButton, accentColor } from "@/components/Holo";
import api, { formatApiErrorDetail } from "@/lib/api";
import IdentityPreview from "@/pages/dashboard/IdentityPreview";
import ProfilePreview from "@/pages/dashboard/ProfilePreview";
import IdentityControls, { IDENTITY_DEFAULTS } from "@/components/IdentityControls";
import ReferenceQuality from "@/components/ReferenceQuality";

const REF_LABELS = ["Secondary Face", "Left Profile", "Right Profile", "Smile", "Full-Body", "Hair", "Body Shape"];

const fileToDataUrl = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(file);
});

const PREVIEWABLE = ["character", "product", "scene"];

export default function VaultModule({
  kind, title, subtitle, accent, lockOptions,
  attrFields = [], attrLabel, attrIcon: AttrIcon, previewLabel, masterLabel = "Master", noun = "profile",
}) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [image, setImage] = useState(null);
  const [locked, setLocked] = useState([]);
  const [attrs, setAttrs] = useState({});
  const [identity, setIdentity] = useState(IDENTITY_DEFAULTS);
  const [refs, setRefs] = useState([]);
  const [refLabel, setRefLabel] = useState(REF_LABELS[0]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();
  const refFileRef = useRef();
  const c = accentColor(accent);
  const previewable = PREVIEWABLE.includes(kind);
  const [previewItem, setPreviewItem] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/vault?kind=${kind}`); setItems(data); }
    catch (e) { console.error("Vault load failed:", e); }
  }, [kind]);
  useEffect(() => { load(); }, [load]);

  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImage(await fileToDataUrl(f));
  };

  const addRef = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const img = await fileToDataUrl(f);
    setRefs((p) => [...p, { label: refLabel, image: img }]);
    e.target.value = "";
  };
  const removeRef = (i) => setRefs((p) => p.filter((_, idx) => idx !== i));

  const toggleLock = (l) => setLocked((p) => p.includes(l) ? p.filter((x) => x !== l) : [...p, l]);

  const save = async () => {
    if (!name.trim()) return toast.error("Name required");
    setSaving(true);
    try {
      const attributes = Object.fromEntries(Object.entries(attrs).filter(([, v]) => v && v.trim()));
      const body = { kind, name, description: desc, image, locked, attributes };
      if (kind === "character") { body.identity_settings = identity; if (refs.length) body.references = refs; }
      await api.post("/vault", body);
      toast.success(`${title} entry locked into vault`);
      setOpen(false); setName(""); setDesc(""); setImage(null); setLocked([]); setAttrs({}); setIdentity(IDENTITY_DEFAULTS); setRefs([]);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    await api.delete(`/vault/${id}`);
    setItems((p) => p.filter((i) => i.id !== id));
    toast("Entry purged");
  };

  return (
    <div data-testid={`module-${kind}`}>
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-display font-bold text-2xl sm:text-3xl">{title}</h1>
          <p className="font-mono-tech text-white/40 text-sm mt-1">{subtitle}</p>
        </div>
        <NeonButton accent={accent} onClick={() => setOpen(!open)} data-testid={`${kind}-add-btn`}>
          <Plus size={14} /> New Entry
        </NeonButton>
      </div>

      {open && (
        <HoloCard accent={accent} className="p-6 mb-8" hover={false}>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-5">
              <div>
                <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">Name</span>
                <input value={name} onChange={(e) => setName(e.target.value)} data-testid={`${kind}-name-input`}
                  className="mt-2 w-full bg-black/30 border-b border-white/20 focus:border-neon-turquoise focus:outline-none px-1 py-2 text-white font-mono-tech text-sm" />
              </div>
              <div>
                <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">Description</span>
                <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} data-testid={`${kind}-desc-input`}
                  className="mt-2 w-full bg-black/30 border border-white/15 focus:border-neon-turquoise focus:outline-none px-3 py-2 text-white font-mono-tech text-sm resize-none" />
              </div>
              <div>
                <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-1"><Lock size={11} /> Lock Attributes</span>
                <div className="flex flex-wrap gap-2 mt-2">
                  {lockOptions.map((l) => (
                    <button key={l} type="button" onClick={() => toggleLock(l)} data-testid={`${kind}-lock-${l}`}
                      className="font-mono-tech text-[10px] uppercase tracking-wider px-2 py-1 border transition-colors"
                      style={locked.includes(l)
                        ? { borderColor: c.hex, color: c.hex, boxShadow: `0 0 10px rgba(${c.rgb},0.3)` }
                        : { borderColor: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.5)" }}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">Reference Image</span>
              <div onClick={() => fileRef.current?.click()} data-testid={`${kind}-upload-zone`}
                className="mt-2 aspect-square border border-dashed border-white/20 flex items-center justify-center cursor-pointer hover:border-neon-turquoise transition-colors overflow-hidden">
                {image ? <img src={image} alt="ref" className="w-full h-full object-cover" /> :
                  <div className="text-center text-white/40 font-mono-tech text-xs"><Upload size={22} className="mx-auto mb-2" /> Drop / click to upload</div>}
              </div>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={pick} />
              {image && kind === "character" && <ReferenceQuality image={image} testid={`${kind}-ref-quality`} />}
            </div>
          </div>
          {kind === "character" && (
            <div className="mt-6 pt-5 border-t border-white/10" data-testid="labeled-references">
              <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/50">Labeled References · improve identity accuracy</span>
              <div className="flex items-center gap-2 mt-3">
                <select value={refLabel} onChange={(e) => setRefLabel(e.target.value)} data-testid="ref-label-select"
                  className="bg-black/40 border border-white/15 px-2 py-1.5 text-white font-mono-tech text-[11px]">
                  {REF_LABELS.map((l) => <option key={l} value={l} className="bg-[#0d0d0f]">{l}</option>)}
                </select>
                <input ref={refFileRef} type="file" accept="image/*" hidden onChange={addRef} />
                <NeonButton onClick={() => refFileRef.current?.click()} data-testid="add-ref-btn"><Plus size={12} /> Add Reference</NeonButton>
              </div>
              {refs.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {refs.map((r, i) => (
                    <div key={i} className="relative border border-white/15 p-1" data-testid={`ref-chip-${i}`}>
                      <img src={r.image} alt={r.label} className="w-14 h-14 object-cover" />
                      <span className="block text-center font-mono-tech text-[8px] uppercase text-white/50 mt-0.5 w-14 truncate">{r.label}</span>
                      <button onClick={() => removeRef(i)} data-testid={`remove-ref-${i}`} className="absolute -top-2 -right-2 bg-black border border-neon-pink/50 text-neon-pink rounded-full p-0.5"><X size={10} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {attrFields.length > 0 && (
            <div className="mt-6 pt-5 border-t border-white/10" data-testid={`${kind}-attributes`}>
              <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] flex items-center gap-1.5" style={{ color: c.hex }}>
                {AttrIcon && <AttrIcon size={12} />} {attrLabel} · locked permanently
              </span>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-3">
                {attrFields.map((f) => (
                  <div key={f}>
                    <span className="font-mono-tech text-[9px] uppercase tracking-wide text-white/40">{f}</span>
                    <input value={attrs[f] || ""} onChange={(e) => setAttrs((p) => ({ ...p, [f]: e.target.value }))} data-testid={`attr-${f}`}
                      className="mt-1 w-full bg-black/30 border-b border-white/15 focus:outline-none px-1 py-1.5 text-white font-mono-tech text-xs"
                      style={{ borderBottomColor: attrs[f] ? c.hex : undefined }} />
                  </div>
                ))}
              </div>
            </div>
          )}
          {kind === "character" && (
            <div className="mt-6 pt-5 border-t border-white/10" data-testid="character-identity-controls">
              <IdentityControls value={identity} onChange={setIdentity} accent={accent} testid="vault-identity" />
            </div>
          )}
          <div className="flex gap-3 mt-6">
            <NeonButton accent={accent} onClick={save} disabled={saving} data-testid={`${kind}-save-btn`}>{saving ? "Locking..." : "Lock Into Vault"}</NeonButton>
            <NeonButton onClick={() => setOpen(false)}>Cancel</NeonButton>
          </div>
        </HoloCard>
      )}

      {items.length === 0 ? (
        <div className="font-mono-tech text-white/30 text-sm border border-white/10 border-dashed p-12 text-center">No entries yet. Lock your first {kind} to begin.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {items.map((it) => (
            <HoloCard key={it.id} accent={accent} className="overflow-hidden group" testid={`vault-item-${it.id}`}>
              <div className="aspect-square bg-black/40 overflow-hidden">
                {it.image ? <img src={it.image} alt={it.name} className="w-full h-full object-cover" /> :
                  <div className="w-full h-full flex items-center justify-center text-white/20 font-mono-tech text-xs">NO IMAGE</div>}
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-display font-bold text-sm truncate">{it.name}</h3>
                  <button onClick={() => remove(it.id)} data-testid={`delete-${it.id}`} className="text-white/30 hover:text-neon-pink transition-colors"><Trash2 size={14} /></button>
                </div>
                {it.locked?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {it.locked.slice(0, 3).map((l) => <span key={l} className="font-mono-tech text-[9px] uppercase px-1.5 py-0.5 border border-white/10 text-white/40">{l}</span>)}
                  </div>
                )}
                {previewable && (
                  it.image ? (
                    <button onClick={() => setPreviewItem(it)} data-testid={`preview-btn-${it.id}`}
                      className="mt-3 w-full flex items-center justify-center gap-2 font-mono-tech text-[10px] uppercase tracking-widest border px-2 py-2 transition-colors"
                      style={{ borderColor: `rgba(${c.rgb},0.4)`, color: c.hex }}>
                      <Sparkles size={12} /> {previewLabel} {it.has_preview && <span className="text-neon-green">✓</span>}
                    </button>
                  ) : (
                    <div className="mt-3 w-full text-center font-mono-tech text-[9px] uppercase tracking-widest border border-white/10 text-white/30 px-2 py-2" data-testid={`preview-disabled-${it.id}`}>
                      Add a reference image to preview
                    </div>
                  )
                )}
              </div>
            </HoloCard>
          ))}
        </div>
      )}

      {previewItem && (
        kind === "character" ? (
          <IdentityPreview item={previewItem} onClose={() => setPreviewItem(null)} onMemoryUpdated={load} />
        ) : (
          <ProfilePreview item={previewItem} accent={accent} title={previewLabel} masterLabel={masterLabel} noun={noun}
            onClose={() => setPreviewItem(null)} onMemoryUpdated={load} />
        )
      )}
    </div>
  );
}
